import { register, init, addMessages, locale as $locale } from "svelte-i18n";
import type { Handle } from "@sveltejs/kit";
import { redirect } from "@sveltejs/kit";
import en from "../../../translations/en.json";
import cfg from "$lib/config/config";
import { getLanguageCode } from "../utils/languageMapping";

register("en", () => import("../../../translations/en.json"));
register("en-DE", () => import("../../../translations/en-de.json"));
register("en-EU", () => import("../../../translations/en-eu.json"));
register("en-AU", () => import("../../../translations/en-row.json"));
register("en-US", () => import("../../../translations/en-us.json"));
register("es", () => import("../../../translations/es.json"));
register("it", () => import("../../../translations/it.json"));
register("fr", () => import("../../../translations/fr.json"));
register("de", () => import("../../../translations/de.json"));
register("de-EU", () => import("../../../translations/de-eu.json"));

const INIT_OPTIONS = {
  fallbackLocale: "en",
  initialLocale: "",
  loadingDelay: 0,
  warnOnMissingMessages: false,
};

export const regionFromPrefs = (
  userPrefs: App.Locals["session"]["userPrefs"],
) =>
  cfg.REGION_ID === "staging"
    ? userPrefs?.region || "staging"
    : getLanguageCode(cfg.REGION_ID);

/**
 * Returns locale extracted from URL pathname
 */
export const getLocaleFromPathname = (pathname: string): string => {
  const path = cfg.BASE_PATH ? pathname.replace(cfg.BASE_PATH, "") : pathname;
  let locale = path.split("/")[1];
  if (!locale || !cfg.LANGS.includes(locale)) {
    locale = cfg.DEFAULT_LOCALE || "en";
  }
  return locale;
};

// initialize i18n library in client
// row is not a valid i18n locale so have set en-row to en-au so it does not error
export async function i18nClientInit(
  userPrefs: App.Locals["session"]["userPrefs"],
) {
  addMessages("en", en);
  const locale = getLocaleFromPathname(window.location.pathname);

  await init({
    ...INIT_OPTIONS,
    initialLocale:
      `${locale}-${regionFromPrefs(userPrefs)}` === "en-row"
        ? "en-au"
        : `${locale}-${regionFromPrefs(userPrefs)}`,
  });
}

/* Sveltekit server hook for i18n middleware */
export const i18nMiddleware: Handle = async ({ event, resolve }) => {
  try {
    const langs = cfg.LANGS;
    let defaultLang = cfg.DEFAULT_LOCALE;
    // Push default lang to end of array,
    // so it doesn't prevent other lang base paths from catching requests
    if (defaultLang) {
      langs.push(langs.splice(langs.indexOf(defaultLang), 1)[0]);
    } else {
      // if we have no default lang, we'll check if we're on staging to set a default 'en' lang
      if (cfg.REGION_ID === "staging" || !cfg.REGION_ID) {
        defaultLang = "en";
        langs.push(langs.splice(langs.indexOf(defaultLang), 1)[0]);
      }
    }

    const url = new URL(event.request.url);
    const locale = getLocaleFromPathname(url.pathname);

    if (locale) {
      event.locals.locale = locale;
      const region = regionFromPrefs(event.locals.session?.userPrefs);
      const fullLocale = `${locale}-${region}`;
      await $locale.set(fullLocale === "en-row" ? "en-au" : fullLocale);
    }

    const response = await resolve(event, {
      transformPageChunk: async ({ html }) => {
        const region = regionFromPrefs(event.locals.session?.userPrefs);
        const fullLocale = `${event.locals.locale}-${region}`;
        const detectedLang =
          fullLocale === "en-row"
            ? "en-au"
            : fullLocale || cfg.DEFAULT_LOCALE || "en";
        return html.replace("%sveltekit.lang%", detectedLang);
      },
    });

    if (locale) {
      response.headers.set("Content-Language", locale);
    }

    // Check if we don't have base locale here and redirect if we haven't got a lang param.
    if (
      cfg.BASE_PATH &&
      !cfg.DEFAULT_LOCALE &&
      langs.length > 0 &&
      !event.params.lang &&
      !event.url.pathname.startsWith("/api") &&
      !event.url.pathname.startsWith("/sitemap.xml")
    ) {
      throw redirect(301, `/${langs[0]}`);
    }

    return response;
  } catch (error) {
    console.error("Error in i18nMiddleware:", error);

    const fallbackLocale = "en";
    await $locale.set(fallbackLocale);

    const response = await resolve(event);
    response.headers.set("Content-Language", fallbackLocale);
    return response;
  }
};
